import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../context/UserContext';
import AuthGateUI from './auth/AuthGateUI';
import { FullPagePreloader } from './ui/Preloader';
import VerifyEmailNotice from './auth/VerifyEmailNotice';
import NotWhitelistedNotice from './auth/NotWhitelistedNotice';
import AuthHandoffStatus, { HandoffStatus } from './auth/AuthHandoffStatus';
import { createCustomTokenFunctionUrl } from '../services/firebase';

interface AuthGateProps {
    children: React.ReactNode;
}

function AuthGate({ children }: AuthGateProps) {
    const { user, isWhitelisted, isLoading } = useUser();

    // Present only when the plugin opened this page (.../callback?nonce=...).
    // Its presence puts us in "plugin login handoff" mode for the whole render.
    const nonce = useMemo(() => new URLSearchParams(window.location.search).get('nonce'), []);
    // The plugin's "Sign Up" link adds mode=register to open on the register form.
    const initialAuthView = useMemo(
        () => new URLSearchParams(window.location.search).get('mode') === 'register' ? 'register' : 'login',
        [],
    );
    const [handoffStatus, setHandoffStatus] = useState<HandoffStatus>('pending');

    // In handoff mode, mint a custom token for the signed-in user and hand it to
    // the plugin: the Cloud Function writes it to Firestore at auth_handoffs/{nonce},
    // which the plugin is subscribed to. No local server / port involved.
    // See web-ui/auth-system.md.
    useEffect(() => {
        // Don't hand a session off to the plugin until the email is verified and
        // the account is allowed past the gate — this is the only place a plugin
        // session is minted, so blocking here keeps the plugin closed too.
        if (!user || !user.emailVerified || !isWhitelisted || !nonce) return;
        const currentUser = user;
        let cancelled = false;

        async function sendTokens() {
            try {
                const idToken = await currentUser.getIdToken();

                const response = await fetch(createCustomTokenFunctionUrl, {
                    method: 'POST',
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ idToken, nonce })
                });

                if (!response.ok) {
                    throw new Error('Failed to hand off custom token');
                }

                if (!cancelled) setHandoffStatus('success');
            } catch (err) {
                console.error('Failed to send tokens to desktop app:', err);
                if (!cancelled) setHandoffStatus('error');
            }
        }
        sendTokens();
        return () => { cancelled = true; };
    }, [user, isWhitelisted, nonce]);

    // Show loading screen while checking auth state
    if (isLoading) {
        return <FullPagePreloader />;
    }

    // Show auth UI if user is not logged in
    if (!user) {
        return <AuthGateUI isLoading={isLoading} initialView={initialAuthView} />;
    }

    // Logged in but email not yet confirmed — block access (and the handoff
    // below) until the address is verified.
    if (!user.emailVerified) {
        return <VerifyEmailNotice user={user} />;
    }

    // Verified, but not on the closed-beta access list — block the app and the
    // handoff below until an admin adds the address.
    if (!isWhitelisted) {
        return <NotWhitelistedNotice user={user} />;
    }

    // Plugin login handoff: this page exists only to pass the session to the
    // plugin, so show the handoff status rather than the full app.
    if (nonce) {
        return <AuthHandoffStatus status={handoffStatus} />;
    }

    // User is authenticated, show the app
    return <>{children}</>;
}

export default AuthGate;
